import { DateTime } from 'luxon'
import { KennoFile } from '@pandiafi/design-system'
import { CountryCode } from '@kenno/resources'

import {
  Beneficiary,
  Contract,
  ContractTenant,
  PropertyCompanyAddressType,
  TerminateContractInput,
} from '../../../../common/validations'
import {
  ContractTerminationDestination,
  ContractType,
} from '../../../../common/types'
import { isPropertyCompanyAddressValid } from '../utils/isPropertyCompanyAddresValid'
import { formatPostalAddress } from '../utils/formatPostalAddress'
import { initialBeneficiaries } from './initialBeneficiaries'
import { getAllowedMonthEndDates } from '../../../utils/terminationDateUtils'

export type BeneficiaryType =
  TerminateContractInput['beneficiaries'][number] & {
    bankCountryCode: CountryCode | null
  }

export interface FormValues extends Omit<
  TerminateContractInput,
  'movingDestination' | 'terminationReason' | 'fileUuids' | 'beneficiaries'
> {
  movingDestination: ContractTerminationDestination | ''
  terminationReason: TerminateContractInput['terminationReason'] | ''
  files: KennoFile[]
  beneficiaries: BeneficiaryType[]
  firstPossibleEndDate: DateTime
  lastPossibleEndDate: DateTime
  currentDate: DateTime
  propertyCompanyName: string | null
  propertyCompanyAddress: string | null
  contractEndDate: string | null
}

const resolveFirstPossibleEndDate = (
  contractType: ContractType,
  now: DateTime,
  contractEndDate: string | null
): DateTime => {
  const isFixedTerm = Boolean(contractEndDate?.trim())

  // For fixed-term contracts: allow early termination starting from today
  if (isFixedTerm) {
    return now.startOf('day')
  }

  // For open-ended contracts: use normal minimum termination date
  const rentalLimitDate = now.plus({ months: 1 }).endOf('month')
  const asoLimitDate = now.plus({ months: 3 }).endOf('day')

  return contractType === ContractType.RENTAL ? rentalLimitDate : asoLimitDate
}

const resolveInitialEndDate = (
  contractType: ContractType,
  now: DateTime,
  contractEndDate: string | null
): DateTime => {
  const rentalLimitDate = now.plus({ months: 1 }).endOf('month')
  const asoLimitDate = now.plus({ months: 3 }).endOf('day')

  const limitDate =
    contractType === ContractType.RENTAL ? rentalLimitDate : asoLimitDate

  const endDateInput = contractEndDate?.trim()
  if (!endDateInput) return limitDate

  const parsed = DateTime.fromISO(endDateInput)
  if (!parsed.isValid) return limitDate

  return parsed > limitDate ? limitDate : parsed
}

const resolveLastPossibleEndDate = (
  now: DateTime,
  contractEndDate: string | null
): DateTime => {
  const limitDate = now.plus({ months: 12 }).startOf('day')
  const today = now.startOf('day')
  const endDateInput = contractEndDate?.trim()
  if (!endDateInput) return limitDate

  const parsed = DateTime.fromISO(endDateInput)
  if (!parsed.isValid) return limitDate

  // Ensure the date is at least today (for fixed-term contracts that may have ended)
  const contractEnd = parsed.endOf('day')
  const minDate = contractEnd > today ? contractEnd : today

  return minDate > limitDate ? limitDate : minDate
}

const initialValues = (
  contract: Contract,
  tenants: ContractTenant[],
  beneficiaries: Beneficiary[],
  // Used for override from i18n translations optionally by customer
  propertyCompanyNameOverride: string | null,
  // Used for override from i18n translations optionally by customer
  propertyCompanyAddressOverride: string | null,
  currentDateTime: DateTime,
  showingDefault: 'tenant' | 'company',
  endDateLastDayOfMonth = true
): FormValues => {
  // In the first version of this form, we only have one tenant so we can hardcode some of the values
  const initialEndDate = resolveInitialEndDate(
    contract.contractType,
    currentDateTime,
    contract.endDate
  )

  const collateralLender = contract.collateral?.bankName || null

  let { propertyCompanyName } = contract

  const isValidPropertyCompanyAddress = isPropertyCompanyAddressValid(
    contract.propertyCompanyAddress
  )

  let formattedPropertyCompanyAddress = isValidPropertyCompanyAddress
    ? formatPostalAddress(
        contract.propertyCompanyAddress as PropertyCompanyAddressType
      )
    : null

  if (propertyCompanyAddressOverride && propertyCompanyNameOverride) {
    propertyCompanyName = propertyCompanyNameOverride
    formattedPropertyCompanyAddress = propertyCompanyAddressOverride
  }

  const displaysSelfDefaultFromConfig = showingDefault === 'tenant'

  const hasNonDisclosureTenant = tenants.some((tenant) => tenant.nonDisclosure)

  // If any tenant has nonDisclosure, always default to "company shows"
  const displaysSelf = hasNonDisclosureTenant
    ? false
    : displaysSelfDefaultFromConfig

  const masterKeyAccessAllowed = !displaysSelf

  const firstPossibleEndDate = resolveFirstPossibleEndDate(
    contract.contractType,
    currentDateTime,
    contract.endDate
  )
  const lastPossibleEndDate = resolveLastPossibleEndDate(
    currentDateTime,
    contract.endDate
  )

  const allowedMonthEndDates = endDateLastDayOfMonth
    ? getAllowedMonthEndDates(firstPossibleEndDate, lastPossibleEndDate)
    : []

  const finalFirstPossibleEndDate =
    firstPossibleEndDate > lastPossibleEndDate
      ? lastPossibleEndDate
      : firstPossibleEndDate

  const resolvedEndDate = endDateLastDayOfMonth
    ? allowedMonthEndDates.length > 0
      ? DateTime.fromJSDate(allowedMonthEndDates[0])
      : finalFirstPossibleEndDate
    : initialEndDate

  return {
    contractType: contract.contractType,
    movingOutDate: null,
    endDate: resolvedEndDate.toFormat('yyyy-MM-dd'),
    firstPossibleEndDate: finalFirstPossibleEndDate,
    lastPossibleEndDate,
    currentDate: currentDateTime,
    displaysSelf,
    presenterTenantUuid: null,
    additionalDetails: '',
    givingContactInfoAllowed: displaysSelf,
    masterKeyAccessAllowed,
    hasPetsInApartment: false,
    terminationReason: '',
    movingDestination: '',
    files: [],
    newAddress: {
      recipientName: '',
      streetAddress: '',
      streetAddressLine2: '',
      postalCode: '',
      city: '',
      countryName: '',
      countryCode: '',
      countrySubdivision: '',
    },
    beneficiaries: initialBeneficiaries({
      initiatingTenantId: contract.tenant.id,
      tenants,
      beneficiaries,
    }),
    isCollateral: null,
    collateralLender,
    collater: contract.collateral,
    propertyCompanyName,
    propertyCompanyAddress: formattedPropertyCompanyAddress,
    contractEndDate: contract.endDate,
  }
}

export default initialValues
